import { Component } from '@theme/component';
import { createSoAtlantaClient, readJsonScript } from '@theme/so-atlanta-api';
import { escapeAttr, escapeHtml, formatCount, setHtml } from '@theme/so-atlanta-renderers';

const OFFICIAL_SO_ATLANTA_TIKTOK_HANDLE = 'so.atlanta.store';

class SoAtlantaCreatorsPage extends Component {
  settings = {};
  client = null;
  connected = false;

  connectedCallback() {
    super.connectedCallback();
    this.connected = true;
    this.settings = readJsonScript(this, '[data-sa-creators-page-settings]');
    this.client = createSoAtlantaClient(this.settings);
    void this.load();
  }

  disconnectedCallback() {
    this.connected = false;
    super.disconnectedCallback();
  }

  async load() {
    const content = this.querySelector('[data-creators-page-content]');
    if (!(content instanceof HTMLElement)) return;

    if (this.settings.enabled === false) {
      content.removeAttribute('aria-busy');
      setHtml(content, this.renderMessage(this.settings.disabledText));
      return;
    }

    try {
      const limit = Math.min(Math.max(Number(this.settings.limit || 50), 3), 100);
      const data = await this.client.trendingCreators({ limit: Math.min(limit + 1, 100) });
      if (!this.connected) return;

      const creators = publicRankedCreators(data?.creators, limit);
      content.removeAttribute('aria-busy');
      setHtml(content, creators.length ? this.renderCreators(creators) : this.renderMessage(this.settings.emptyText));
    } catch (error) {
      console.warn('So Atlanta creators page failed', error);
      if (!this.connected) return;
      content.removeAttribute('aria-busy');
      setHtml(content, this.renderMessage(this.settings.errorText));
    }
  }

  renderCreators(creators) {
    const podium = creators.slice(0, 3);
    const rest = creators.slice(3);
    return `
      <div class="sa-creators-page__podium" aria-label="Top creators">
        ${podium.map((creator, index) => this.renderPodiumCreator(creator, index)).join('')}
      </div>
      <div class="sa-creators-page__directory">
        <div class="sa-creators-page__directory-head">
          <h2>${escapeHtml(this.settings.listHeading || 'Creator rankings')}</h2>
          <p>${escapeHtml(`${creators.length} ranked creators`)}</p>
        </div>
        <div class="sa-creators-page__list" role="list">
          ${rest.map((creator, index) => this.renderListCreator(creator, index + 4)).join('')}
        </div>
      </div>
    `;
  }

  renderPodiumCreator(creator, index) {
    const rank = Number(creator?.rank || index + 1);
    const normalized = normalizeCreator(creator, rank);
    const podiumClass = rank === 1 ? 'first' : rank === 2 ? 'second' : 'third';
    return `
      <article class="sa-creator-podium sa-creator-podium--${podiumClass}" style="--podium-level: ${podiumHeight(rank)}">
        <a class="sa-creator-podium__profile" href="${escapeAttr(normalized.profileUrl)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeAttr(`Open ${normalized.name} on TikTok`)}">
          <span class="sa-creator-podium__medal">${escapeHtml(rankLabel(rank))}</span>
          <span class="sa-creator-podium__avatar">
            ${renderAvatar(normalized, 'lazy')}
          </span>
          <span class="sa-creator-podium__name">${escapeHtml(normalized.name)}</span>
          <span class="sa-creator-podium__handle">${escapeHtml(normalized.displayHandle)}</span>
          ${normalized.rankMovement ? renderRankMovementBadge(normalized.rankMovement, 'sa-creator-podium__movement') : ''}
        </a>
        <div class="sa-creator-podium__stats">
          ${renderStat('Videos used', videoCountText(normalized.videoCount))}
          ${normalized.followerCount != null ? renderStat('Followers', formatCount(normalized.followerCount)) : ''}
        </div>
        <div class="sa-creator-podium__base" aria-hidden="true">
          <span>#${escapeHtml(String(rank))}</span>
        </div>
      </article>
    `;
  }

  renderListCreator(creator, fallbackRank) {
    const normalized = normalizeCreator(creator, fallbackRank);
    return `
      <a class="sa-creator-row" href="${escapeAttr(normalized.profileUrl)}" target="_blank" rel="noopener noreferrer" role="listitem" aria-label="${escapeAttr(`Open ${normalized.name} on TikTok`)}">
        <span class="sa-creator-row__rank">#${escapeHtml(String(normalized.rank))}</span>
        <span class="sa-creator-row__avatar">${renderAvatar(normalized, 'lazy')}</span>
        <span class="sa-creator-row__identity">
          <span class="sa-creator-row__name">${escapeHtml(normalized.name)}</span>
          <span class="sa-creator-row__meta">
            <span class="sa-creator-row__handle">${escapeHtml(normalized.displayHandle)}</span>
            ${normalized.rankMovement ? renderRankMovementBadge(normalized.rankMovement, 'sa-creator-row__movement') : ''}
          </span>
        </span>
        <span class="sa-creator-row__stat">${escapeHtml(videoCountText(normalized.videoCount))}</span>
        <span class="sa-creator-row__stat sa-creator-row__stat--optional">
          ${normalized.followerCount != null ? `${escapeHtml(formatCount(normalized.followerCount))} followers` : 'Followers pending'}
        </span>
      </a>
    `;
  }

  renderMessage(message) {
    return `<p class="sa-creators-page__message">${escapeHtml(message || '')}</p>`;
  }
}

function normalizeCreator(creator, fallbackRank) {
  const handle = cleanHandle(creator?.handle);
  const name = String(creator?.name || (handle ? `@${handle}` : 'Creator')).trim();
  const profileUrl = String(creator?.profileUrl || (handle ? `https://www.tiktok.com/@${handle}` : '')).trim() || '#';
  const avatarUrl = String(creator?.avatarUrl || '').trim();
  const rank = Number(creator?.rank || fallbackRank);
  const followerCount = creator?.followerCount == null ? null : Number(creator.followerCount);
  const rankMovement = normalizeRankMovement(creator?.rankMovement);
  return {
    avatarUrl,
    displayHandle: handle ? `@${handle}` : '@creator',
    followerCount: Number.isFinite(followerCount) ? followerCount : null,
    initials: initialsFor(name, handle),
    name,
    profileUrl,
    rank: Number.isFinite(rank) ? rank : fallbackRank,
    rankMovement,
    videoCount: Number(creator?.videoCount || 0),
  };
}

function renderAvatar(creator, loading) {
  if (creator.avatarUrl) {
    return `<img src="${escapeAttr(creator.avatarUrl)}" alt="" loading="${escapeAttr(loading)}">`;
  }
  return `<span>${escapeHtml(creator.initials)}</span>`;
}

function renderStat(label, value) {
  return `
    <span class="sa-creator-stat">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </span>
  `;
}

function renderRankMovementBadge(rankMovement, className) {
  const isDirectional = rankMovement.direction === 'up' || rankMovement.direction === 'down';
  const arrow = rankMovement.direction === 'up' ? '&uarr;' : '&darr;';
  const content = isDirectional
    ? `<span class="sa-rank-movement__arrow" aria-hidden="true">${arrow}</span><span>${escapeHtml(String(rankMovement.positions))}</span>`
    : escapeHtml(rankMovement.label);

  return `
    <span class="${escapeAttr(className)} ${escapeAttr(`${className}--${rankMovement.direction}`)}" aria-label="${escapeAttr(`Rank movement in the 24-hour top 100: ${rankMovement.label}`)}">
      ${content}
    </span>
  `;
}

function normalizeRankMovement(value) {
  if (!value || typeof value !== 'object') return null;
  const direction = ['up', 'down', 'same', 'new'].includes(String(value.direction))
    ? String(value.direction)
    : 'same';
  const rawPositions = Number(value.positions || 0);
  const positions = Number.isFinite(rawPositions) ? rawPositions : 0;
  const label = String(value.label || fallbackRankMovementLabel(direction, positions)).trim();
  if (!label || direction === 'same') return null;
  return { direction, label, positions: Math.max(0, Math.round(Math.abs(positions))) };
}

function fallbackRankMovementLabel(direction, positions) {
  if (direction === 'new') return 'New entrant';
  if (direction === 'up') return `Up ${Math.max(0, positions)}`;
  if (direction === 'down') return `Down ${Math.max(0, positions)}`;
  return 'No change';
}

function videoCountText(count) {
  const value = Number(count || 0);
  return value === 1 ? '1 video used' : `${value} videos used`;
}

function rankLabel(rank) {
  if (rank === 1) return 'Gold';
  if (rank === 2) return 'Silver';
  if (rank === 3) return 'Bronze';
  return `#${rank}`;
}

function podiumHeight(rank) {
  if (rank === 1) return '1';
  if (rank === 2) return '0.78';
  return '0.62';
}

function initialsFor(name, handle) {
  const source = String(name || handle || 'Creator').replace(/^@+/, '').trim();
  return source.slice(0, 2).toUpperCase();
}

function cleanHandle(value) {
  return String(value || '').trim().replace(/^@+/, '');
}

function normalizedHandle(value) {
  return cleanHandle(value).toLowerCase();
}

function handleFromTikTokUrl(value) {
  const match = String(value || '').match(/tiktok\.com\/@([^/?#]+)/i);
  return match ? match[1] : '';
}

function isOfficialSoAtlantaCreator(creator) {
  const handle = normalizedHandle(creator?.handle) || normalizedHandle(handleFromTikTokUrl(creator?.profileUrl));
  return handle === OFFICIAL_SO_ATLANTA_TIKTOK_HANDLE;
}

function publicRankedCreators(creators, limit) {
  return (Array.isArray(creators) ? creators : [])
    .filter((creator) => !isOfficialSoAtlantaCreator(creator))
    .slice(0, limit)
    .map((creator, index) => ({ ...creator, rank: index + 1 }));
}

if (!customElements.get('so-atlanta-creators-page')) {
  customElements.define('so-atlanta-creators-page', SoAtlantaCreatorsPage);
}
